/**
 * The Supply language layer — Bahasa Malaysia by default, English opt-in,
 * persisted in the `fb_supply_lang` cookie. BM-default is a product decision
 * (the floor crew reads BM; desk/manage staff who prefer EN opt in once and
 * it sticks): every string in the app is authored as a `Copy` pair and routed
 * through `pick`, so there is exactly one place a caller can pick a language.
 *
 * PURE file — zero `next/headers`, safe to import from a "use client"
 * component (the lang-cookie sync + header toggle land here in the next
 * task). Split the same way outlet-cookie.ts splits from outlet-server.ts:
 * the server-only half (`getSupplyLang`, `hasLangCookie`) lives in
 * ./lang-server.ts, which imports `normalizeLang`/`SUPPLY_LANG_COOKIE` from
 * here rather than duplicating them. Importing `cookies` at module scope in
 * this file — even unused by the client — would drag `next/headers` into
 * every client bundle that imports `pick`/`normalizeLang`, so it must never
 * come back here.
 *
 * NOT read by the layout boot script. `app/supply/layout.tsx` stamps
 * `data-supply-lang` PRE-PAINT straight off `document.cookie`, mirroring the
 * theme/tier/nav mechanism, precisely so no module in this split ever has to
 * run in the static layout.
 */

export type SupplyLang = "ms" | "en";
export type Copy = { ms: string; en: string };

export const SUPPLY_LANG_COOKIE = "fb_supply_lang";
export const SUPPLY_LANG_COOKIE_MAX_AGE = 60 * 60 * 24 * 365;

/**
 * Cookie value -> language, tolerant by design (matches theme/tier's
 * normalize* helpers). Only the exact lowercase "en" opts in; anything else
 * — undefined, null, "EN", "xx", a stray blank — falls back to the BM
 * default. Case-sensitive on purpose: the cookie is only ever written by
 * this app's own sync component, so a mismatched case means a bad write
 * upstream, not a user preference worth honouring.
 */
export function normalizeLang(raw: string | undefined | null): SupplyLang {
  return raw === "en" ? "en" : "ms";
}

/** The one place a caller selects a language out of a `Copy` pair. */
export function pick(lang: SupplyLang, copy: Copy): string {
  return copy[lang];
}

/** Fill `{name}` slots in a picked string; an unknown slot is left as written. */
export function fillCopy(template: string, values: Record<string, string | number>): string {
  return template.replace(/\{(\w+)\}/g, (_, key: string) => String(values[key] ?? `{${key}}`));
}

/**
 * The one place a language CHANGE gets written: the pre-paint dataset stamp
 * (instant visual feedback — the boot script reads this same attribute name
 * on the next load) plus the cookie. Mirrors `theme.ts`'s `applySupplyTheme`
 * — same shape, same reason (one writer, not one per caller). PURE function
 * that touches `document`/`document.cookie`, so — like `applySupplyTheme` —
 * it must only ever be called from a "use client" event handler; nothing
 * here runs at import time, so defining it in this otherwise-pure file is
 * safe. Does NOT call `router.refresh()` itself: unlike theme (CSS-only, no
 * server re-render needed), a language change swaps server-rendered TEXT, so
 * every caller still has to trigger its own refresh afterwards.
 */
export function applySupplyLang(root: HTMLElement | null, next: SupplyLang): void {
  if (root) root.dataset.supplyLang = next;
  document.cookie = `${SUPPLY_LANG_COOKIE}=${next}; path=/; max-age=${SUPPLY_LANG_COOKIE_MAX_AGE}`;
}
